'use client';

import React, { useEffect, useRef } from 'react';
import {
  BannerText,
  IBannerText,
} from '@/components/molecules/BannerText/BannerText';
import { usePageStore } from '@/store/usePageStore';
import { BackToTop } from '@/components/molecules/BackToTop/BackToTop';
import { TPageContentPaddingTop } from '@/types/global';
import { ScriptStructuredData } from '@/components/atoms/ScriptStructuredData/ScriptStructuredData';
import { useTranslations } from 'next-intl';
import { isHomePage, isWorkPage } from '@/utils/page';
import { usePathname } from 'next/navigation';
import { Footer } from '@/components/molecules/Footer/Footer';
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { 
  GallerySliderModal,
  IGallerySliderModalProps,
} from '../GallerySliderModal/GallerySliderModal';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ModalNavigationAlert } from '@/components/molecules/ModalNavigationAlert/ModalNavigationAlert';
import { emailEncoded } from '@/utils/constants';
import { useFiltersStore } from '@/store/useFiltersStore';
import * as Styled from './PageContent.styled';


export interface IPageContent {
  pageBackground?: 'main' | 'obsolete' | 'black';
  headerBgTransparentOnStart?: boolean;
  includePrefooterBanner?: boolean;
  isBlockedByLogin?: boolean;
  includeBackToTop?: boolean;
  contentPaddingTop?: TPageContentPaddingTop;
  structuredData?: object;
  isProjectPage?: boolean;
  gallerProps?: IGallerySliderModalProps;
  isPrivatePresentation?: boolean;
  prefooter?: {
    title?: string;
  }
  children?: React.ReactNode;
}

export const PageContent: React.FC<IPageContent> = ({
  pageBackground = 'main',
  headerBgTransparentOnStart = false,
  includePrefooterBanner = true,
  includeBackToTop = true,
  contentPaddingTop = 'base',
  isBlockedByLogin = false,
  structuredData,
  isProjectPage = false,
  gallerProps,
  isPrivatePresentation = false,
  prefooter,
  children,
}) => {
  const t = useTranslations('prefooter');
  const tFooter = useTranslations('footer');
  const contentBannerText = t.raw('bannerText') as IBannerText;

  // const shouldDisplayFooter = usePageStore(
  //   (state) => state.shouldDisplayFooter
  // );
  const isUserLogged = usePageStore((s) => s.isUserLogged);
  const gsapTimeout = usePageStore((s) => s.gsapTimeout);
  const isMobileMenuOpen = usePageStore((s) => s.isMobileMenuOpen);
  const setIsMobileMenuOpen = usePageStore((s) => s.setIsMobileMenuOpen);
  const refMain = useRef<HTMLElement>(null);
  const isContentBlockedByLoggin = isBlockedByLogin && !isUserLogged;
  const pathname = usePathname();
  const isPresentationMode = usePageStore(
    (s) => s.isPresentationMode
  );

  useGSAP(() => {
    setTimeout(() => {
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    }, gsapTimeout);
  }, []);

  useEffect(() => {

    if (typeof window === 'undefined') return;
    
    const pos = usePageStore.getState().scrollPosition; 
    if (pos == null) return;

    const smoother = ScrollSmoother.get();

    // In homepage, scroll to saves position for projects grid
    if (isHomePage(pathname)) {
      if (smoother) {
        requestAnimationFrame(() => {
          smoother.scrollTo(pos, false); 
        });
      } else {
        window.scrollTo({ top: pos, behavior: "smooth" });
      }
      usePageStore.getState().setScrollPosition(undefined); 
    }
  }, [pathname, isProjectPage, isPresentationMode]);

  useGSAP(() => {
    gsap.to(refMain.current, {
      opacity: 1,
      duration: 0.15,
    });
  }, [pathname]);

  useEffect(()=> {
    if (!isWorkPage(pathname) && !isPresentationMode) {
      useFiltersStore.getState().resetFiltersAndProjects();
    }

  }, [pathname, isPresentationMode])

  return (
    <>
      <div id="smooth-wrapper" className={isPrivatePresentation ? 'presentation-page' : ''}>
        <div id="smooth-content">
          <Styled.Main
            ref={refMain}
            contentPaddingTop={
              isContentBlockedByLoggin ? 'none' : contentPaddingTop
            }
            {...(pageBackground === 'black'
              ? { 'data-theme': 'dark' }
              : pageBackground === 'obsolete'
                ? { 'data-theme': 'obsolete' }
                : {})}
            data-header-transparent={headerBgTransparentOnStart}
            data-version="1"
          >
            {isContentBlockedByLoggin ? (
              <Styled.BlockedByLogin>
                <BannerText 
                  borderTop={false}
                  title={{
                    en: 'You are not allowed to access this content',
                    es: 'No tienes permiso para acceder a este contenido',
                  }} textLg={{
                    en: `Please contact us at ${emailEncoded}`,
                    es: `Por favor contáctanos en ${emailEncoded}`,
                  }} 
                />
              </Styled.BlockedByLogin>
            ) : (
              children
            )}
            {includePrefooterBanner && (
              <BannerText
                className="prefooter-banner"
                {...contentBannerText}
                {...(!includeBackToTop && { marginBottom: 'xl' })}
                {...prefooter?.title && { titleLg: prefooter.title }}
                animationContainer={{
                  type: 'fadeInAndUp',
                }}
                animations={{
                  titleLg: {
                    type: 'revealUp',
                  },
                  subtitle: {
                    type: 'fadeInAndUp',
                  },
                }}
                marginBottomOnMobile='lg'
              />
            )}
            {includeBackToTop && (
              <BackToTop text={tFooter('backToTopText')} marginBottom="xl" />
            )}
          </Styled.Main>
          {!isPresentationMode && <Footer />}
        </div>
      </div>
      {gallerProps && <GallerySliderModal {...gallerProps} />}
      {isPresentationMode && <ModalNavigationAlert />}
      {structuredData && <ScriptStructuredData content={structuredData} />}
    </>
  );
};
